import React, { useEffect, useState } from 'react';
import ModalBody from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { LoadNature } from '../../../../services/apiSLTP/materials/loadNatures';
import { crudStatus } from '../../../../utils/enums';
import { useCreateLoadNatureMutation, useUpdateLoadNatureMutation } from '../../../../hooks/mutations/useLoadNatureMutation';
import ToggleLine from '../../../ToggleLine';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import DropDown, { OptionType } from '../../../DropDown';
import { useWasteCodesQuery } from '../../../../hooks/queries/useWasteCodesQuery';

interface ModalProps {
    loadNature: LoadNature | null;
    loadNatures: LoadNature[];
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    showEditModal: boolean;
    refetch: () => void;
}

const UpdateLoadNatureModal: React.FC<ModalProps> = ({ loadNature, isEditMode = true, showEditModal, setShowEditModal, loadNatures, refetch }) => {
    const { t } = useTranslation();
    const [designation, setDesignation] = useState(loadNature?.designation || '');
    const [status, setStatus] = useState(loadNature?.crudStatus || crudStatus.OK);
    const [wasteCode, setWasteCode] = useState<OptionType | null>(
        loadNature?.defaultWasteCodeId ? { label: loadNature.defaultWasteCodeId.designation, value: loadNature.defaultWasteCodeId._id } : null
    );
    const [displayOrder, setDisplayOrder] = useState(typeof loadNature?.displayOrder === 'number' ? loadNature?.displayOrder.toString() : '');

    const { mutate: mutateLoadNature } = useUpdateLoadNatureMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating loadNature:', error.message),
    });

    const { mutate: addLoadNature } = useCreateLoadNatureMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating loadNature:', error.message),
    });

    const { data: dataWasteCodes, isLoading: isWastCodesLoading } = useWasteCodesQuery({ queries: { active: true } });

    const handleActiveChange = (isActive: boolean) => {
        setStatus(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    const isDuplicateDesignation = () => {
        return loadNatures.some((ln) => ln._id !== loadNature?._id && ln.designation === designation);
    };

    return (
        <ModalBody
            title={t('loadNature')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!designation.trim() || isDuplicateDesignation() || !wasteCode}
            handleOk={() => {
                const data = {
                    designation,
                    crudStatus: status,
                    displayOrder: displayOrder ? parseInt(displayOrder) : null,
                    defaultWasteCodeId: wasteCode ? wasteCode.value : null,
                };
                isEditMode ? mutateLoadNature({ loadNatureId: loadNature?._id || '', data }) : addLoadNature(data);
            }}
        >
            <CustomTextInput
                value={designation}
                label={t('name')}
                onChange={setDesignation}
                errorText={isDuplicateDesignation() ? t('error_duplicate') : ''}
                required
            />
            <ToggleLine checked={status === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />

            <DropDown
                items={dataWasteCodes?.map((wasteCode) => ({ label: `${wasteCode.designation} - ${wasteCode.description}`, value: wasteCode._id })) || []}
                selectedItem={wasteCode}
                handleChange={(event, value) => setWasteCode(value)}
                inputLabel={t('wasteCode')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateLoadNatureModal;
